<!-- eslint-disable no-plusplus -->
<!-- eslint-disable comma-dangle -->
<!-- eslint-disable indent -->
<template>
  <AppEventContainer :show-offline="true">
    <Notification class="text-xl my-2" @closed="message = ''" :message="message" :message-type="messageType"
      :details="messageDetails" />
    <div class="flex justify-center">
      <div class="check-in w-full max-w-md px-4">
        <div class="m-2" v-if="isLastStopForExtra">
          <div class="flex justify-center">
            <AppRouterLink
              title="Purchase Extra Card"
              route-name="EventAddons"
              :route-params="{ id: currentPokerRunEvent?.id }"
              class="my-2"
            />
          </div>
        </div>
        <div v-if="unassignedRegistrations.length > 0" class="mb-4">
          <div class="font-medium text-normal-emphasis mb-2">
            We have found registrations that have not been assigned.
          </div>
          <div class="flex justify-between items-center">
            <label class="font-medium text-normal-emphasis mr-2">Would you like to assign these to your account?
            </label>
            <AppSwitch toggleId="assignRegistrations" v-model="assignRegistrations" />
          </div>
        </div>
        <div class="font-medium text-normal-emphasis my-2">Enter code to get your card.</div>
        <div class="mb-2">
          <CodeInput v-model="checkInCode" @entered="submitCheckIn" />
        </div>
        <div class="flex justify-center">
          <AppButton
            title="Get Your Card"
            :is-loading="isBusy"
            class="mt-2 mb-2 w-full md:w-auto"
            @click="submitCheckIn"
          />
        </div>
        <div class="font-medium text-normal-emphasis mt-4">Or scan QR code</div>
        <div class="flex justify-center">
          <Scanner
            :is-busy="isBusy"
            label="Scan"
            @scanned="scanCode"
            @scanned-error="handleScanError"
          />
        </div>
      </div>
    </div>
  </AppEventContainer>
</template>

<script setup lang="ts">
import { ref } from "vue";
import * as Sentry from "@sentry/vue";
import { useRouter, useRoute } from "vue-router";
import { useEventParticipantStore } from "@/store/EventParticipant";
import { useUserProfileStore } from "@/store/UserProfile";
import Notification from "@/components/Notification.vue";
import CodeInput from "@/components/CodeInput.vue";
import Scanner from "@/components/Scanner.vue";
import useManageFile from "@/modules/useManageFile";
import { storeToRefs } from "pinia";
import { Registration } from "@models/Registration";
import RegistrationDataService from "@/services/RegistrationDataService";
import useDate from "@/modules/useDate";
import AppButton from "@/components/AppButton.vue";
import AppRouterLink from "@/components/AppRouterLink.vue";

const { formatISODate, formatISODatetime } = useDate();

const router = useRouter();
const route = useRoute();
const {
  checkIn,
  getEventParticipantContainer,
  loadEventParticipant,
} = useEventParticipantStore();
const { currentPokerRunEvent, ownerRegistrations } = storeToRefs(
  useEventParticipantStore()
);
const { openFile } = useManageFile();
const { profile } = useUserProfileStore();

const checkInCode = ref("");
const message = ref("");
const messageDetails = ref("");
const messageType = ref("info");
const assignRegistrations = ref(false);
const isBusy = ref(false);
const isLastStopForExtra = ref(false);
const unassignedRegistrations = ref<Registration[]>([]);

const handleCheckin = async (content: string, metadata: any) => {
  isBusy.value = true;
  try {
    if (content !== null && (await checkIn(content, metadata))) {
      if (assignRegistrations.value && unassignedRegistrations.value.length > 0) {
        const promises = [] as Promise<void>[];
        unassignedRegistrations.value.forEach((registration) => {
          promises.push(
            RegistrationDataService.claimAssignment(
              registration.eventId as string,
              registration.redemptionCode as string
            )
          );
        });
        await Promise.all(promises);
      }

      message.value = "You have successfully checked in.";
      messageType.value = "success";
      setTimeout(() => {
        isBusy.value = false;

        const routeName = currentPokerRunEvent.value.eventSettings.gameFormat === "Trade Show" ? "RegisteredEventLite" : "RegisteredEvent";
        router.push({ name: routeName, params: { id: currentPokerRunEvent.value.id } });

      }, 2000);
    } else {
      isBusy.value = false;
      messageType.value = "warning";
      message.value = "Invalid check in code.";
    }
  } catch (ex) {
    if (ex.errorType === "warning") {
      messageType.value = ex.errorType;
      message.value = ex.message;
    } else {
      Sentry.captureException(ex);
      messageType.value = "danger";
      message.value = "Error checking in.";
      messageDetails.value = ex?.message;
    }
  }
  isBusy.value = false;
};

const scanCode = async (content: string) => {
  if (content !== null && content !== "") {
    if (content.indexOf("code=") > -1) {
      const lastPart = content.split("code=")[1];
      await handleCheckin(lastPart, { checkinFrom: "qr" });

    } else {
      const lastPart = content.lastIndexOf("/");
      await handleCheckin(content.substring(lastPart + 1), { checkinFrom: "qr" });
    }
  }
};

const submitCheckIn = async () => {
  await handleCheckin(checkInCode.value, { checkinFrom: "entered" });
};

const handleScanError = (scanError: string) => {
  messageType.value = "danger";
  message.value = scanError;
};

const initialize = async () => {
  if (!currentPokerRunEvent.value || currentPokerRunEvent.value.id !== route.params.id) {
    await loadEventParticipant(route.params.id as string, profile.id);
  }

  if (route.params.code) {
    checkInCode.value = route.params.code as string;
  }

  if (route.query.code) {
    checkInCode.value = route.query.code as string;
  }

  const eventParticipantsContainer = getEventParticipantContainer();
  if (
    currentPokerRunEvent.value.eventSettings.enableExtraCard &&
    currentPokerRunEvent.value.eventSettings.allowExtraCardPurchaseAfterRegistration &&
    !eventParticipantsContainer.hasExtraCard()
  ) {
    const cardDealt = eventParticipantsContainer.getMaxCardsDealt();
    isLastStopForExtra.value = cardDealt === 2;
    messageType.value = "info";
    message.value = isLastStopForExtra.value
      ? "This is your last chance to purchase an extra card. You will not be able to purchase an extra after you checkin to this stop."
      : "";
  }

  if (checkInCode.value && !isLastStopForExtra.value) {
    await submitCheckIn();
  }

  unassignedRegistrations.value = ownerRegistrations.value.filter(
    (x) => x.status !== "accepted"
  );
};
initialize();
</script>
